import React, { createContext, useContext, useState, ReactNode } from "react";
import { useMutation } from "convex/react";
import { api } from "../api";
import { useAuth } from "./AuthContext";

interface Device {
  id: string;
  name: string;
  model: string;
  iosVersion: string;
  isConnected: boolean;
  hasFindMyEnabled: boolean;
  hasPasscode: boolean;
  hasExistingProfiles: boolean;
  serial?: string;
  udid?: string;
}

interface DeviceContextType {
  devices: Device[];
  selectedDevice: Device | null;
  isScanning: boolean;
  scanForDevices: () => void;
  selectDevice: (device: Device) => void;
  checkFindMyStatus: () => Promise<boolean>;
  updateDeviceStatus: (updates: Partial<Device>) => void;
  supervise: (udid: string, apps: { allow: string[]; block: string[] }, onSupervisionComplete?: () => void) => Promise<void>;
  fetchInstalledApps: (udid: string) => Promise<string[]>;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const useDevice = () => {
  const context = useContext(DeviceContext);
  if (context === undefined) {
    throw new Error("useDevice must be used within a DeviceProvider");
  }
  return context;
};

export const DeviceProvider = ({ children }: { children: ReactNode }) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isSupervising, setIsSupervising] = useState(false);
  const setSerialMutation = useMutation(api.devices.registrations.setSerial);
  const { code } = useAuth();

  const scanForDevices = async () => {
    setIsScanning(true);
    console.log("Scanning for devices...");

    try {
      // Check if we're running in webview with native bindings
      if (typeof window !== "undefined" && "getDevices" in window) {
        const nativeDevices = await (window as any).getDevices();
        const mappedDevices: Device[] = nativeDevices.map((device: any) => ({
          id: device.udid || device.serial || Math.random().toString(36),
          name: device.name || "Unknown Device",
          model: device.model || "iPhone",
          iosVersion: device.os || "Unknown",
          isConnected: true,
          hasFindMyEnabled: true, // Default assumption
          hasPasscode: true, // Default assumption
          hasExistingProfiles: false, // Default assumption
          serial: device.serial,
          udid: device.udid,
        }));
        setDevices(mappedDevices);
      } else {
        // Fallback to mock data for development
        const mockDevices: Device[] = [
          {
            id: "device-1",
            name: "Emma's iPhone",
            model: "iPhone 14",
            iosVersion: "17.2",
            isConnected: true,
            hasFindMyEnabled: true,
            hasPasscode: true,
            hasExistingProfiles: false,
          },
        ];
        setDevices(mockDevices);
      }
    } catch (error) {
      console.error("Error scanning for devices:", error);
      setDevices([]);
    }

    setIsScanning(false);
  };

  const selectDevice = (device: Device) => {
    setSelectedDevice(device);
  };

  const checkFindMyStatus = async (): Promise<boolean> => {
    // Simulate checking Find My status
    console.log("Checking Find My status...");
    return selectedDevice?.hasFindMyEnabled || false;
  };

  const updateDeviceStatus = (updates: Partial<Device>) => {
    if (selectedDevice) {
      const updatedDevice = { ...selectedDevice, ...updates };
      setSelectedDevice(updatedDevice);
      setDevices(devices.map((d) => (d.id === selectedDevice.id ? updatedDevice : d)));
    }
  };

  const fetchInstalledApps = async (udid: string): Promise<string[]> => {
    if (typeof window !== "undefined" && "listApps" in window) {
      try {
        console.log("Fetching installed apps for UDID:", udid);
        const apps = await (window as any).listApps(udid);
        console.log(`Found ${apps.length} installed apps`);
        return apps;
      } catch (error) {
        console.warn("Could not fetch installed apps:", error);
        return [];
      }
    }
    console.log("No native listApps binding available");
    return [];
  };

  const supervise = async (udid: string, apps: { allow: string[]; block: string[] }, onSupervisionComplete?: () => void) => {
    // Prevent duplicate supervision calls
    if (isSupervising) {
      console.log("Supervision already in progress, ignoring duplicate call");
      return;
    }

    setIsSupervising(true);
    try {
      console.log("Starting supervision with:", { udid, allowedApps: apps.allow.length, blockedApps: apps.block.length });

      // Backend registration to get invitation (single call)
      let invitation: string | undefined;
      if (selectedDevice?.serial && code) {
        const setSerialParams = { code, serial: selectedDevice.serial, apps };
        console.log("Calling setSerial with parameters:", setSerialParams);
        try {
          invitation = await setSerialMutation(setSerialParams);
        } catch (backendError: any) {
          const errorMessage = backendError?.message || backendError?.data?.message || backendError?.toString() || "";
          if (
            errorMessage.includes("already registered") || 
            errorMessage.includes("Device already registered") ||
            errorMessage.includes("Server Error")
          ) {
            console.error("Device already registered:", backendError);
            throw new Error("It looks like this phone's serial number is already in our system. Please reach out to customer support for further help.");
          } else {
            console.error("Backend registration failed:", backendError);
            throw new Error("It looks like this phone's serial number is already in our system. Please reach out to customer support for further help.");
          }
        }
      } else {
        console.log("Missing serial or code, skipping setSerial call", {
          hasSerial: !!selectedDevice?.serial,
          hasCode: !!code,
        });
        throw new Error("Missing device serial or access code for registration");
      }

      // Step 3: Native supervision with invitation
      if (typeof window !== "undefined" && "supervise" in window) {
        const superviseData = {
          udid,
          invitation,
          supervise: true,
          removable: false,
        };
        try {
          await (window as any).supervise(JSON.stringify(superviseData));
          console.log("Native supervision completed for UDID:", udid);
        } catch (nativeError) {
          console.error("Native supervision failed:", nativeError);
          if (nativeError && typeof nativeError === "string" && nativeError.includes("MBErrorDomain")) {
            throw new Error(`MDM Profile installation failed: ${nativeError}`);
          }
          throw new Error(`Device supervision failed: ${nativeError}`);
        }
      } else {
        console.log("No native supervise binding available");
      }

      // Step 4: Only call completion callback after BOTH steps succeed
      console.log("Device supervision and registration completed successfully for UDID:", udid);
      if (onSupervisionComplete) {
        onSupervisionComplete();
      }
    } catch (error) {
      console.error("Error during device supervision:", error);
      throw error;
    } finally {
      setIsSupervising(false);
    }
  };

  const value = {
    devices,
    selectedDevice,
    isScanning,
    scanForDevices,
    selectDevice,
    checkFindMyStatus,
    updateDeviceStatus,
    supervise,
    fetchInstalledApps,
  };

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
};
